import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useWatch } from 'react-hook-form';
import {
    RequestEmpty,
    RequestError,
    RequestErrorBanner,
    RequestLoading,
} from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { PermissionGate } from '@/shared/ui/PermissionGate/PermissionGate';
import {
    CHAT_MESSAGE_DEFAULT_VALUES,
    chatMessageSchema,
    type ChatMessageFormValues,
} from '@/entities/Chat/model/schema';

import {
    formatChatMessageTime,
    groupChatMessagesByDay,
} from '@/entities/Chat/model/selectors';

import type {
    ChatCompanion,
    DisplayChatMessage,
    ChatMessageGroup,
} from '@/entities/Chat/model/types';
import classes from './ChatWorkspace.module.scss';
import { MessageText } from '@/shared/ui/MessageText/MessageText';

interface ChatConversationProps {
    companion: Pick<ChatCompanion, 'id' | 'name'> | null;
    messages: DisplayChatMessage[];
    status: RequestStatus;
    error: string | null;
    viewMessagesError?: string | null;
    sendStatus?: RequestStatus;
    sendError?: string | null;
    isInert?: boolean;
    readOnly?: boolean;
    headerActions?: ReactNode;
    historyControls?: ReactNode;
    renderMessageMetadata?: (message: DisplayChatMessage) => ReactNode;
    groups?: ChatMessageGroup<DisplayChatMessage>[];

    /** Показывает время сообщения в часовом поясе выбранного представления */
    formatMessageTime?: (timestamp: number) => string;
    onSend?: (message: string) => void | Promise<void>;
    onRetryMessage?: (messageId: string) => void | Promise<void>;
    onRetryConversation: () => void;
    onRetryViewMessages?: () => void;
    onOpenDetails?: () => void;
    onOpenImage: (imageUrl: string) => void;
}

/** Определяет локальное сообщение без подтверждения отправки */
const isPendingMessage = (
    message: DisplayChatMessage,
): message is Extract<DisplayChatMessage, { status: 'pending' | 'failed' }> => (
    'status' in message
);

/** Отображает сообщения выбранного диалога */
export const ChatConversation = (
    {
        companion,
        messages,
        status,
        error,
        viewMessagesError = null,
        sendStatus = RequestStatus.Idle,
        sendError = null,
        isInert = false,
        readOnly = false,
        headerActions,
        historyControls,
        renderMessageMetadata,
        groups,
        formatMessageTime = formatChatMessageTime,
        onSend,
        onRetryMessage,
        onRetryConversation,
        onRetryViewMessages,
        onOpenDetails,
        onOpenImage,
    }: ChatConversationProps
) => {
    const form = useUnsavedForm<ChatMessageFormValues>({
        resolver: zodResolver(chatMessageSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: CHAT_MESSAGE_DEFAULT_VALUES,
    }, !readOnly);

    const canSendMessages = useCanAccessPermission('admin.chats.send');
    const formRef = useRef<HTMLFormElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messageRegistration = form.register('message');

    const draft = useWatch({
        control: form.control,
        name: 'message'
    }) ?? '';

    const messageGroups = useMemo(() => groups ?? groupChatMessagesByDay(messages), [groups, messages]);
    const isSending = sendStatus === RequestStatus.Loading || form.formState.isSubmitting;

    const canSend = !readOnly && canSendMessages && Boolean(companion)
        && status === RequestStatus.Success
        && form.formState.isValid
        && !isSending;

    const inputError = form.formState.errors.message?.message;
    const composerError = inputError ?? sendError;

    const setTextareaRef = useCallback((element: HTMLTextAreaElement | null): void => {
        messageRegistration.ref(element);
        textareaRef.current = element;
    }, [messageRegistration]);

    useEffect(() => {
        form.reset(CHAT_MESSAGE_DEFAULT_VALUES);
    }, [companion?.id, form]);

    const firstMessageRef = useRef<string>();
    const lastMessageRef = useRef<string>();
    const previousCompanionRef = useRef<string>();

    const scrollPositionRef = useRef({
        top: 0,
        height: 0
    });

    useEffect(() => {
        const container = messagesContainerRef.current;

        if (!container) return;

        const first = messages[0]?.id;
        const last = messages[messages.length - 1]?.id;

        const prepended = previousCompanionRef.current === companion?.id
            && firstMessageRef.current && first !== firstMessageRef.current
            && last === lastMessageRef.current;

        container.scrollTop = prepended
            ? scrollPositionRef.current.top + container.scrollHeight - scrollPositionRef.current.height
            : container.scrollHeight;
        firstMessageRef.current = first;
        lastMessageRef.current = last;
        previousCompanionRef.current = companion?.id;
        scrollPositionRef.current = {
            top: container.scrollTop,
            height: container.scrollHeight
        };
    }, [messages, companion?.id]);

    useEffect(() => {
        if (draft.length === 0 && textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, [draft]);

    const submitMessage = form.handleSubmit(async ({ message }) => {
        if (!canSend) return;

        form.reset(CHAT_MESSAGE_DEFAULT_VALUES);
        await onSend?.(message);
    });

    /** Отправляет сообщение по Enter без переноса строки */
    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            formRef.current?.requestSubmit();
        }
    };

    /** Подстраивает высоту поля под текст сообщения */
    const resizeTextarea = (): void => {
        const textarea = textareaRef.current;

        if (!textarea) return;

        textarea.style.height = 'auto';

        const borderHeight = textarea.offsetHeight - textarea.clientHeight;
        textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;
    };

    return (
        <section
            className={classes.conversationPanel}
            aria-label="Диалог"
            aria-hidden={isInert || undefined}
            {...(isInert
                ? { inert: '' }
                : {})}
        >
            <header className={classes.conversationHeader}>
                <h2>{companion?.name ?? 'Выберите чат'}</h2>
                {headerActions}
                {companion && onOpenDetails && (
                    <ActionButton
                        type="button"
                        color="secondary"
                        size="small"
                        iconOnly
                        icon="pi pi-info-circle"
                        aria-label="Показать информацию о пользователе"
                        title="Информация о пользователе"
                        onClick={onOpenDetails}
                    />
                )}
            </header>

            <div
                ref={messagesContainerRef}
                onScroll={(event) => {
                    const container = event.currentTarget;
                    scrollPositionRef.current = {
                        top: container.scrollTop,
                        height: container.scrollHeight
                    };
                }}
                className={classes.messages}
                role="log"
                aria-live="polite"
                aria-relevant="additions text"
            >
                {historyControls}
                {!companion && (
                    <RequestEmpty message="Выберите чат, чтобы прочитать сообщения." />
                )}
                {companion && status === RequestStatus.Loading && (
                    <RequestLoading message="Загружаем сообщения..." />
                )}
                {companion && (
                    <RequestErrorBanner
                        status={status}
                        error={error}
                        fallbackMessage="Не удалось загрузить сообщения"
                        onRetry={onRetryConversation}
                        compact={false}
                    />
                )}
                {companion && status === RequestStatus.Success && messages.length === 0 && (
                    <RequestEmpty message="В этом чате пока нет сообщений." />
                )}
                {companion && status === RequestStatus.Success && error && (
                    <div
                        className={classes.inlineError}
                        role="alert"
                    >
                        {error}
                    </div>
                )}
                {companion && status === RequestStatus.Success && viewMessagesError && (
                    <RequestError
                        compact
                        message={viewMessagesError}
                        retryLabel="Повторить отметку"
                        onRetry={onRetryViewMessages}
                    />
                )}
                {companion && status === RequestStatus.Success && messageGroups.map((group) => (
                    <div
                        key={group.key}
                        className={classes.messageGroup}
                    >
                        <div
                            className={classes.dateSeparator}
                            role="separator"
                        >
                            <span>{group.label}</span>
                        </div>
                        {group.messages.map((message) => {
                            const pendingStatus = isPendingMessage(message)
                                ? message.status
                                : null;

                            return (
                                <article
                                    key={message.id}
                                    className={`
                                        ${classes.messageBubble} 
                                        ${message.isMine
                                            ? classes.messageSent
                                            : classes.messageReceived}
                                        ${pendingStatus === 'failed'
                                            ? classes.messageFailed
                                            : ''}`
                                    }
                                    aria-label={message.isMine
                                        ? 'Исходящее сообщение'
                                        : 'Входящее сообщение'
                                    }
                                >
                                    <div className={classes.messageBody}>
                                        {message.photoUrls.map((imageUrl) => (
                                            <button
                                                key={imageUrl}
                                                type="button"
                                                className={classes.messageImageButton}
                                                aria-label="Открыть изображение"
                                                onClick={() => onOpenImage(imageUrl)}
                                            >
                                                <img
                                                    src={imageUrl}
                                                    alt="Вложение к сообщению"
                                                />
                                            </button>
                                        ))}
                                        {message.message && (
                                            <p className={classes.messageText}>
                                                <MessageText text={message.message} />
                                            </p>
                                        )}
                                        <footer className={classes.messageFooter}>
                                            <span>
                                                {pendingStatus === 'pending' && 'Отправляется…'}
                                                {pendingStatus === 'failed' && 'Не отправлено'}
                                            </span>
                                            <time dateTime={new Date(message.createdAt).toISOString()}>
                                                {formatMessageTime(message.createdAt)}
                                            </time>
                                            {renderMessageMetadata?.(message)}
                                        </footer>
                                        {!readOnly && pendingStatus === 'failed' && (
                                            <ActionButton
                                                type="button"
                                                className={classes.retryMessageAction}
                                                color="danger"
                                                size="small"
                                                outlined
                                                label="Повторить отправку"
                                                disabled={isSending}
                                                onClick={() => void onRetryMessage?.(message.id)}
                                            />
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ))}
            </div>

            {!readOnly &&
                <PermissionGate permission="admin.chats.send">
                    <form
                        ref={formRef}
                        className={classes.composer}
                        onSubmit={(event) => void submitMessage(event)}
                    >
                        <label
                            htmlFor="chat-message"
                            className={classes.visuallyHidden}
                        >
                            Сообщение
                        </label>
                        <textarea
                            {...messageRegistration}
                            ref={setTextareaRef}
                            id="chat-message"
                            rows={3}
                            disabled={!companion || status !== RequestStatus.Success || isSending}
                            placeholder="Введите сообщение..."
                            aria-invalid={Boolean(inputError)}
                            aria-describedby={composerError
                                ? 'chat-send-error'
                                : undefined
                            }
                            onInput={resizeTextarea}
                            onKeyDown={handleKeyDown}
                        />
                        <ActionButton
                            type="submit"
                            color="success"
                            label={isSending
                                ? 'Отправка…'
                                : 'Отправить'
                            }
                            loading={isSending}
                            disabled={!canSend}
                        />
                        {composerError && (
                            <span
                                id="chat-send-error"
                                className={classes.composerError}
                                role="alert"
                            >
                                {composerError}
                            </span>
                        )}
                    </form>
                </PermissionGate>
            }
        </section>
    );
};
